import { profileStartup } from '../diagnostics/startup-profile';
import { beginStartupWork, finishStartupWork } from '../diagnostics/startup-work';
import { barsOf, saySpan } from './spark-bars';
import { isNoise } from './trend-view';
import { Прочерк } from '../ui/nothing';

/**
 * Столбики заказов по дням и изменение к прошлому промежутку.
 *
 * ЗАЧЕМ СТОЛБИКИ, ЕСЛИ РЯДОМ ЕСТЬ ЧИСЛО ЗАКАЗОВ. Одно число за период
 * не отличает кампанию, которая ровно приносит по три заказа в день,
 * от той, что дала двадцать в первый день и умерла. Обе покажут
 * шестьдесят, и обе выглядят одинаково — до тех пор, пока не увидишь
 * форму.
 *
 * НАПРАВЛЕНИЕ ВАЖНЕЕ УРОВНЯ. «ДРР 21 %» не говорит ничего: у одного
 * товара это отлично, у другого разорение. «ДРР 21 %, и он вырос вдвое
 * за две недели» — говорит всё, и говорит срочно.
 */

/**
 * Размеры — из эталона: столбик 6 px с радиусом 2, промежуток 3,
 * высота поля 26. У нас было три пикселя без промежутка и без радиуса,
 * и восемь дней слипались в одну зубчатую полоску, по которой формы
 * не прочитать — а столбики ставят ровно ради формы.
 */
const H = 26;
const BAR = 6;
const GAP = 3;

/**
 * Ширина столбца «Динамика» — ПЕРЕМЕННОЙ ЭТАЛОНА, а не числом.
 *
 * БЕЗ ШИРИНЫ ВООБЩЕ СТОЛБЕЦ СЖИМАЛСЯ ПО САМОМУ УЗКОМУ СОДЕРЖИМОМУ.
 * У кампании без заказов столбиков нет вовсе, и весь столбец
 * схлопывался до ширины слова «Динамика», а подписи «ДРР ↑202 %»
 * переносились по слогам в три строки и раздували строку таблицы
 * втрое. Владелец это и увидел на снимке 18.08.2026.
 *
 * ЧИСЛО ЗДЕСЬ СТОЯЛО СВОЁ — 132, посчитанное под четырнадцать
 * столбиков. В эталоне столбец 84 (`colDef`), столбиков восемь.
 * Теперь число одно на всех и лежит там же, где ширины соседних
 * столбцов: `--col-dyn` в `tokens.css`.
 */
export const SPARK_WIDTH = 'var(--col-dyn)';

export function Spark({
  values,
  trend,
}: {
  values: number[];
  trend: { drr: number | null; cpo: number | null };
}) {
  const startupWorkStarted = beginStartupWork();
  const bars = barsOf(values);
  const max = Math.max(...bars.map((b) => b.orders), 1);

  /**
   * ═══════════════════════════════════════════════════════════════════
   * ПУСТАЯ КЛЕТКА — ЭТО НЕ «НЕТ ЗАКАЗОВ», ЭТО «ОТРИСОВКА СЛОМАЛАСЬ».
   *
   * У кампании, заведённой вчера, истории нет: столбиков ноль, обе
   * подписи о переменах молчат (сравнивать не с чем), и клетка
   * «Динамика» выходила пустой совсем. В таблице из тридцати строк
   * одна дырка среди графиков читается как сбой, а не как ответ.
   *
   * Тот же довод, что и у единицы высоты у дня без заказов
   * несколькими строками ниже: пустое место и ноль — разные вещи,
   * и разница должна быть видна.
   *
   * Нашла проба `probe/kabinet-edge.mjs`, случай
   * «Кампании-трудная-строка»: кампания без чисел вообще.
   * ═══════════════════════════════════════════════════════════════════
   */
  if (bars.length === 0 && isNoise(trend.drr) && isNoise(trend.cpo)) {
    return profileStartup('spark.children', finishStartupWork('spark.render', startupWorkStarted, (
      <Прочерк почему="Сравнивать не с чем: за выбранный промежуток заказов не было" />
    )));
  }

  return profileStartup('spark.children', finishStartupWork('spark.render', startupWorkStarted, (
    /* ШИРИНЫ ЗДЕСЬ НЕТ, И ЭТО НАРОЧНО: её держит сама клетка столбца
       (`--col-dyn`), как в эталоне. Ширина на внутреннем узле, равная
       ширине столбца, вылезала бы за поля клетки на все шестнадцать
       пикселей полей. */
    <span className="flex flex-col items-start gap-0.5">
      {bars.length > 0 && (
        <span className="flex items-end" style={{ height: H, gap: GAP }}>
          {bars.map((b, i) => (
            <i
              key={i}
              title={`${b.orders} зак. за ${saySpan(b.days)}`}
              style={{
                width: BAR,
                borderRadius: 2,
                // ЕДИНИЦА ВЫСОТЫ У ДНЯ БЕЗ ЗАКАЗОВ — НЕ УКРАШЕНИЕ.
                // Пустое место читается как «данных нет», а тонкая
                // черта — как «ноль заказов», и это разные вещи.
                height: Math.max(1, Math.round((b.orders / max) * H)),
                /* ЦВЕТ ГРАФИКА, А НЕ СМЫСЛОВОЙ ЗЕЛЁНЫЙ. В эталоне
                   столбики — `--series-1`: это ряд данных, а не оценка.
                   Зелёный здесь читался как «хорошо», хотя столбик
                   говорит только «в этот день было столько заказов». */
                background: b.orders > 0 ? 'var(--series-1)' : 'var(--line-strong)',
                display: 'inline-block',
              }}
            />
          ))}
        </span>
      )}
      <span className="flex flex-col rk-text-xs">
        <Delta label="ДРР" pct={trend.drr} />
        <Delta label="заказ" pct={trend.cpo} />
      </span>
    </span>
  )));
}

/**
 * У ДРР И ЦЕНЫ ЗАКАЗА РОСТ — ЭТО ПЛОХО, и покрасить их как обычный рост
 * значит показать зелёным ровно то, из-за чего кампанию стоит остановить.
 */
function Delta({ label, pct }: { label: string; pct: number | null }) {
  if (isNoise(pct)) return null;
  const v = pct!;
  return (
    /**
     * ПОДПИСЬ НЕ ПЕРЕНОСИТСЯ. «ДРР ↑202 %» переносилось по слогам
     * в три строки — «ДРР ↑202», «%», «заказ ↑122», «%» — и раздувало
     * строку таблицы втрое. Знак процента, оторванный от числа
     * и стоящий отдельной строкой, не значит вообще ничего.
     *
     * НЕРАЗРЫВНЫЙ ПРОБЕЛ ПЕРЕД ЗНАКОМ, а не обычный: так набирают
     * проценты по-русски, и он же не даёт им разъехаться, если
     * `nowrap` когда-нибудь снимут.
     */
    <span
      style={{ color: v < 0 ? 'var(--good-ink)' : 'var(--bad-ink)', whiteSpace: 'nowrap' }}
    >
      {label} {v > 0 ? '↑' : '↓'}
      {Math.abs(Math.round(v))}&nbsp;%
    </span>
  );
}
